import ApiInstance from './instance'
import {
  ICollectionHistory,
  ICollectionTask,
  IResident,
  IResidentContact,
  IResidentCreatePaymentMethodDTO,
  IResidentEventHistoryFiltersDTO,
  IResidentFiltersDTO,
  IResidentListResponse,
  IResidentOpenBalanceDTO,
  IResidentPaymentMethod,
  ISetupWizardFacilityResidentContactSettings,
  URL_MAPPING
} from '@zunta/configurations'

const { resident: residentUrls, mail: mailUrls } = URL_MAPPING

export default class ResidentApi {
  instance: ApiInstance

  constructor (instance: ApiInstance) {
    this.instance = instance
  }

  async getResidentHistoryByFilters (residentId: number | string, filters: IResidentEventHistoryFiltersDTO): Promise<{ total: number, items: ICollectionHistory[] }> {
    return this.instance.doGet(`${residentUrls.prefix}${residentUrls.getResidentEventHistory.path}/${residentId}`, filters)
  }

  async getResidentsInCollections (): Promise<IResident[]> {
    return this.instance.doGet(`${residentUrls.prefix}${residentUrls.getResidentsInCollection.path}`)
  }

  async getResidentOpenBalance (residentId: number | string): Promise<IResidentOpenBalanceDTO> {
    return this.instance.doGet(`${residentUrls.prefix}${residentUrls.getOpenBalance.path}/${residentId}`)
  }

  async getResidentFilteredList (filters: IResidentFiltersDTO): Promise<IResidentListResponse> {
    return this.instance.doGet(`${residentUrls.prefix}${residentUrls.list.path}`, filters, null, { isCompressed: true })
  }

  async getResidentTypeOfContactList (facilityId: number): Promise<Array<string>> {
    return this.instance.doGet(`${residentUrls.prefix}${residentUrls.typeOfContactList.path}`, { facilityId })
  }

  async getChainTypeOfContactListMap (): Promise<{ [k: string]: string[] }> {
    return this.instance.doGet(`${residentUrls.prefix}${residentUrls.chainTypeOfContactListMap.path}`)
  }

  async sendCollectionLetterMails (
    templateId: string | number,
    residentId: string | number,
    mailContacts: IResidentContact[],
    emailContacts: IResidentContact[],
    assignedTo: string | undefined
  ): Promise<'OK'> {
    return this.instance.doPost(`${mailUrls.prefix}/sendCollectionMailLetter/${residentId}`, {
      template: templateId,
      emailContacts,
      mailContacts,
      assignedTo
    })
  }

  async sendCollectionStatementMails (
    statementId: string | number,
    residentId: string | number,
    mailContacts: IResidentContact[],
    emailContacts: IResidentContact[],
    assignedTo: string | undefined
  ): Promise<'OK'> {
    return this.instance.doPost(`${mailUrls.prefix}/sendCollectionMailStatement/${residentId}`, {
      statement: statementId,
      emailContacts,
      mailContacts,
      assignedTo
    })
  }

  async setupFacilityResidentContactSettings (residentContactSettings: ISetupWizardFacilityResidentContactSettings[]): Promise<'OK'> {
    return this.instance.doPost(`${residentUrls.prefix}${residentUrls.setupFacilityResidentContactSettings.path}`, { residentContactSettings })
  }

  async getResidentContacts (residentId: number | string): Promise<Array<IResidentContact>> {
    return this.instance.doGet(`${residentUrls.prefix}${residentUrls.getResidentContacts.path}/${residentId}`, {}, 'getResidentContacts')
  }

  async makeResidentContactForMailing (residentId: number, contactId: number): Promise<Array<IResidentContact>> {
    return this.instance.doPut(`${residentUrls.prefix}${residentUrls.makeResidentContactForMailing.path}/${residentId}/${contactId}`)
  }

  async makeResidentContactPrimary (residentId: number, contactId: number): Promise<Array<IResidentContact>> {
    return this.instance.doPut(`${residentUrls.prefix}${residentUrls.makeResidentContactPrimary.path}/${residentId}/${contactId}`)
  }

  async updateResidentSettings (residentId: number, settings: Partial<IResident>, affectedBatch?: number): Promise<'OK' | any> {
    return this.instance.doPut(`${residentUrls.prefix}${residentUrls.updateSettings.path}/${residentId}`, { settings, affectedBatch })
  }

  async initResidentContactsUsedForMailing (facilityId: number | string, order: string[], override: boolean): Promise<'OK'> {
    return this.instance.doPut(`${residentUrls.prefix}${residentUrls.initResidentContactsUsedForMailing.path}/${facilityId}`, {
      order,
      override
    })
  }

  async getResidentById (residentId: number | string): Promise<IResident> {
    return this.instance.doGet(`${residentUrls.prefix}${residentUrls.getResident.path}/${residentId}`, {}, 'getResidentById')
  }

  async pushResidentPageToMirror (residentId: number | string, mirrorRoomId: string): Promise<IResident> {
    return this.instance.doGet(`${residentUrls.prefix}/pushResidentPageToMirror/${mirrorRoomId}/${residentId}`, {}, 'pushResidentToMirror')
  }

  async checkForActiveMirror (mirrorRoomId: string): Promise<string> {
    return this.instance.doGet(`${residentUrls.prefix}/checkForActiveMirror/${mirrorRoomId}`, {}, 'checkForActiveMirror')
  }

  async fetchFacilityResidents (facilityId: string | number) {
    return this.instance.doPost(`${residentUrls.prefix}${residentUrls.fetchFacilityResidents.path}/${facilityId}`)
  }

  async createPaymentMethod (residentId: number | string, data: IResidentCreatePaymentMethodDTO): Promise<IResidentPaymentMethod> {
    return this.instance.doPost(`${residentUrls.prefix}${residentUrls.addPaymentMethod.path}/${residentId}`, data)
  }

  async getResidentPaymentMethods (residentId: number | string): Promise<IResidentPaymentMethod[]> {
    return this.instance.doGet(`${residentUrls.prefix}${residentUrls.getResidentPaymentMethods.path}/${residentId}`)
  }

  async makeResidentPaymentMethodPrimary (residentId: number | string, paymentMethodId: string | number) {
    return this.instance.doPut(`${residentUrls.prefix}${residentUrls.makeResidentPaymentMethodPrimary.path}/${residentId}/${paymentMethodId}`)
  }

  async deletePaymentMethod (paymentMethodId: number | string) {
    return this.instance.doDelete(`${residentUrls.prefix}${residentUrls.deletePaymentMethod.path}/${paymentMethodId}`)
  }

  async getResidentCollectionTask (residentId: number | string): Promise<ICollectionTask> {
    return this.instance.doGet(`${residentUrls.prefix}${residentUrls.getResidentCollectionTask.path}/${residentId}`)
  }

  async getUserResidents (): Promise<IResident[]> {
    return this.instance.doGet(`${residentUrls.prefix}/getUserResidents`)
  }

  async editPaymentMethod (residentId: number | string, paymentMethodId: string | number, data: IResidentCreatePaymentMethodDTO) {
    return this.instance.doPut(`${residentUrls.prefix}/editPaymentMethod/${residentId}/${paymentMethodId}`, data)
  }

  async toggleResidentContactEmailForMailing (residentId: number, contactId: number, isEmailUsedForMailing: boolean) {
    return this.instance.doPut(`${residentUrls.prefix}/toggleResidentContactEmailForMailing/${residentId}/${contactId}`, { isEmailUsedForMailing })
  }

  async toggleResidentContactAddressForMailing (residentId: number, contactId: number, isAddressUsedForMailing: boolean) {
    return this.instance.doPut(`${residentUrls.prefix}/toggleResidentContactAddressForMailing/${residentId}/${contactId}`, { isAddressUsedForMailing })
  }

  async updateResidentContactsForMailing (residentId: number | string, payload: any) {
    return this.instance.doPut(`${residentUrls.prefix}/updateContacts/${residentId}`, payload)
  }

  async getResidentChainMigrationData (residentId: number) {
    return this.instance.doGet(`${residentUrls.prefix}/getResidentChainMigrationData/${residentId}`) as Promise<Partial<IResident> | null>
  }
}
